import { noop } from 'lodash'
import debounce from 'lodash/fp/debounce'
import { Loader2, X } from 'lucide-react'
import useOutsideClick from 'hooks/useOutsideClick'
import FieldIcon from 'components/form/CommonFormField/FieldIcon'
import {
  ReactNode,
  ForwardedRef,
  forwardRef,
  useEffect,
  useState,
  useMemo,
  useRef,
  ChangeEventHandler,
  InputHTMLAttributes,
} from 'react'
import {
  cn,
  Command,
  CommandList,
  CommandEmpty,
  CommandItem,
  Input,
  Button,
} from '@wellesley/ui'

type AutocompleteProps<TOption> = {
  value?: TOption
  inputValue?: string
  onChange?: (opt: TOption | null) => void
  onInputChange?: (value: string) => void
  name?: string
  icon?: JSX.Element
  placeholder?: string
  options?: TOption[]
  minChars?: number
  debounceMs?: number
  isLoading?: boolean
  getOptionKey: (opt: TOption) => string
  getOptionLabel: (opt: TOption) => string
  renderOption?: (opt: TOption) => ReactNode
  className?: string
  inputClassName?: string
  disableClearable?: boolean
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>

function AutocompleteInner<TOption>(
  {
    value,
    inputValue,
    onChange,
    onBlur,
    onInputChange = noop,
    name,
    icon,
    placeholder = 'Search...',
    options = [],
    minChars = 3,
    debounceMs = 300,
    disabled,
    isLoading = false,
    getOptionKey,
    getOptionLabel,
    renderOption,
    className = '',
    inputClassName = '',
    disableClearable,
    ...rest
  }: AutocompleteProps<TOption>,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const containerRef = useRef<HTMLDivElement | null>(null)

  const [open, setOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const initialSearchText = useMemo(
    () => inputValue || (value ? getOptionLabel(value) : ''),
    [value, inputValue, getOptionLabel],
  )
  const [searchText, setSearchText] = useState<string>(initialSearchText)

  useOutsideClick(containerRef, handleClose)

  useEffect(() => {
    setSearchText(initialSearchText)
  }, [initialSearchText])

  const debouncedInputChange = useMemo(
    () => debounce(debounceMs)(onInputChange),
    [debounceMs, onInputChange],
  )

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const textValue = e.target.value
    setSearchText(textValue)

    if (textValue.length >= minChars) {
      setOpen(true)
      debouncedInputChange(textValue)
    } else if (textValue === '') {
      if (!disableClearable) {
        handleClearSelection()
      }
    }
  }

  const handleSelect = (option: TOption) => {
    const label = getOptionLabel(option)
    setSearchText(label)
    setOpen(false)

    onChange && onChange(option)
  }

  function handleClearSelection() {
    setSearchText('')
    setOpen(false)
    onChange && onChange(null)
  }

  function handleClose() {
    setOpen(false)
    setSearchText(initialSearchText)
  }

  return (
    <Command
      className={cn('relative h-auto overflow-visible', className)}
      shouldFilter={false}
      ref={containerRef}
    >
      <div>
        <Input
          ref={ref}
          data-testid={`search-${name}`}
          value={searchText}
          disabled={disabled}
          placeholder={placeholder}
          className={cn(inputClassName, !!icon && 'pl-11')}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            onBlur?.(e)
            setIsFocused(false)
          }}
          {...rest}
        />

        <FieldIcon
          icon={icon}
          disabled={disabled}
          isFocused={isFocused}
          isFilled={!!value}
        />

        {!disableClearable && (value || inputValue) && (
          <Button
            variant="none"
            size="none"
            type="button"
            className="px-xs absolute right-0 top-1/2 h-full -translate-y-1/2 text-gray-500 hover:text-gray-700"
            onClick={() => handleClearSelection()}
            data-testid="autocomplete-clear-button"
          >
            <X className="size-5 rounded-full bg-white p-[2px]" />
          </Button>
        )}
      </div>
      <CommandList
        className={cn(
          'z-100 p-xxs rounded-m absolute top-full max-h-48 w-full border bg-white',
          { hidden: !open },
        )}
      >
        {open && (
          <>
            <CommandEmpty>
              {isLoading ? (
                <Loader2
                  size={20}
                  className="mx-auto flex-shrink-0 animate-spin"
                />
              ) : (
                searchText && 'No results found'
              )}
            </CommandEmpty>
            {!isLoading &&
              options?.map((opt) => (
                <CommandItem
                  key={getOptionKey(opt)}
                  className="!rounded-m max-w-full cursor-pointer"
                  onSelect={() => handleSelect(opt)}
                  value={getOptionKey(opt)}
                >
                  {renderOption ? renderOption(opt) : getOptionLabel(opt)}
                </CommandItem>
              ))}
          </>
        )}
      </CommandList>
    </Command>
  )
}

const Autocomplete = forwardRef(AutocompleteInner) as <T>(
  props: AutocompleteProps<T> & {
    ref?: ForwardedRef<HTMLInputElement>
  },
) => JSX.Element

export default Autocomplete
